import React from "react";
import { cn } from "@/lib/utils";
import { AcademicianStartupButton } from "@/components/academician/academicianstartup-button";
import { BellIcon, AlertTriangleIcon } from "lucide-react";

interface FounderAttention {
  id: string;
  name: string;
  avatar?: string;
  daysSinceLogin: number;
  lastActivity: string;
  progress: number;
}

interface FoundersAttentionListProps {
  /**
   * List of founders needing attention
   */
  founders?: FounderAttention[];

  /**
   * Send reminder callback
   */
  onSendReminder?: (founderId: string) => void;

  /**
   * Title
   */
  title?: string;

  /**
   * Additional CSS classes
   */
  className?: string;
}

const defaultFounders: FounderAttention[] = [];

export function FoundersAttentionList({
  founders = defaultFounders,
  onSendReminder,
  title = "Founders Needing Attention",
  className,
}: FoundersAttentionListProps) {
  const handleSendReminder = async (founderId: string, founderName: string) => {
    if (onSendReminder) {
      onSendReminder(founderId);
      return;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    try {
      const response = await fetch(`/admin/founders/${founderId}/send_reminder`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
      });
      if (response.ok) {
        alert(`Reminder sent to ${founderName}`);
      } else {
        alert('Failed to send reminder. Please try again.');
      }
    } catch {
      alert('Failed to send reminder. Please try again.');
    }
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <AlertTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-500" />
          {title}
        </h3>
        <p className="text-sm text-muted-foreground">
          Founders with low activity or long time since last login
        </p>
      </div>

      {/* Founders list */}
      <div className="space-y-3">
        {founders.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">All founders are active! 🎉</p>
          </div>
        ) : (
          founders.map((founder) => (
            <div
              key={founder.id}
              className="flex items-center gap-3 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors"
            >
              {/* Avatar */}
              {founder.avatar ? (
                <img src={founder.avatar} alt={founder.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                  {founder.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
              )}

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-semibold text-foreground">
                    {founder.name}
                  </p>
                  <span
                    className={cn(
                      "text-xs font-medium px-2 py-0.5 rounded-full",
                      founder.daysSinceLogin >= 10
                        ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                        : founder.daysSinceLogin >= 5
                          ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
                          : "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                    )}
                  >
                    {founder.daysSinceLogin}d ago
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Last: {founder.lastActivity}
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">
                        Progress
                      </span>
                      <span className="text-xs font-medium text-foreground">
                        {founder.progress}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${founder.progress}%` }}
                      />
                    </div>
                  </div>
                  <AcademicianStartupButton
                    variant="secondary"
                    size="sm"
                    leftIcon={<BellIcon />}
                    onClick={() => handleSendReminder(founder.id, founder.name)}
                  >
                    Remind
                  </AcademicianStartupButton>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
